





































































































































import { Vue, Component, Prop, Emit, State } from 'nuxt-property-decorator';
import { PropType } from 'vue';
import Link from '../../toc/links/Link.ts';
import CloseBtn from '../CloseBtn.vue';
import { Settings } from '@/types/initialData';
import { NoteHighlight } from '@/types/highlightContract';
import localConfig from '@/store/localConfig.store';
import NoteItemTools from '@/components/ui/side-panel/notes/NoteItemTools.vue';
import ReadMore from '@/components/ui/side-panel/notes/ReadMore.vue';
import TooltipButton from '@/components/ui/TooltipButton.vue';
import { pageExistsInTree } from '@/components/ui/highlight/utils/pageExists';
import { prettyFormatTimestamp } from '@/util/timestampFormat';
import { TeacherMenu, TeacherMenuLink } from '@/types/pageContract';
import KeyHandleEventBus from '@/store/keyHandle.eventbus';

@Component({
  components: { CloseBtn, NoteItemTools, ReadMore, TooltipButton }
})
export default class NoteItem extends Vue {
  @Prop({ type: Object as PropType<NoteHighlight>, required: true })
  note!: NoteHighlight;

  @Prop({ type: String, default: '' })
  selectedNoteId!: boolean;

  @Prop({ type: Boolean, default: false })
  setLimit!: boolean;

  @Prop({ type: Boolean, default: false })
  canShowFullPreview!: boolean;

  @Prop({ type: Boolean, default: false })
  forceShowNavigation!: boolean;

  @Prop({ type: Boolean, default: false })
  showCloseBtn!: boolean;

  @Prop({ type: Boolean, default: false })
  alwaysShowTools!: boolean;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.navigation[0] || [])
  navMain: Link;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.site?.settings)
  settings!: Settings;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.teachermenu)
  teacherMenu: TeacherMenu;

  showActions: boolean = false;
  hoveringAction: boolean = false;
  showDeletePrompt: boolean = false;
  dontShowAgain: boolean = false;
  contentIsScrolled: boolean = false;

  get link(): Link | null {
    const pid = Number(this.note.pid);
    const queue = [];
    queue.push(this.navMain);
    let link: Link;
    while ((link = queue.pop())) {
      if (link && link.uid === pid) {
        return link;
      }
      if (link.children && link.children.length) {
        for (let i = 0; i < link.children.length; i++) {
          queue.push(link.children[i]);
        }
      }
    }
    return null;
  }

  get noteComment(): string {
    return this.note.userComment
      ? this.note.userComment
      : (this.$i18n.t('labels.notes.no-content') as string);
  }

  get disableDeleteNotePromptTime(): Date | null {
    return localConfig.disableDeleteNotePromptTime;
  }

  get prettyFormattedTimestamp(): string {
    return prettyFormatTimestamp(
      new Date(this.note.createdTimestamp),
      this.$tc('labels.generic.time'),
      this.$i18n.localeProperties.code
    );
  }

  get aboutPagePid(): string {
    return this.settings.aboutPagePid.toString();
  }

  get pageIsInTeacherMenu(): TeacherMenuLink | null {
    return this.findPageInTeacherMenu(this.teacherMenu.children, this.note.pid);
  }

  get pageExists(): boolean {
    if (this.note.pid === this.aboutPagePid || this.pageIsInTeacherMenu) {
      return true;
    } else {
      return pageExistsInTree(this.navMain, Number(this.note.pid));
    }
  }

  @Emit('edit-note')
  editNote(): NoteHighlight {
    return this.note;
  }

  @Emit('delete-note')
  deleteNote(): NoteHighlight {
    if (this.dontShowAgain) {
      const currentDate = new Date();
      const fiveMinutesLater = new Date(currentDate.getTime() + 5 * 60 * 1000);
      this.setDisableDeleteNotePromptTime(fiveMinutesLater);
    } else {
      this.showDeletePrompt = false;
    }
    return this.note;
  }

  @Emit('navigate-to-highlight')
  navigateToHighlight(): NoteHighlight {
    return this.note;
  }

  @Emit('delete-prompt-open')
  keepOpenWhenPromptActive(): boolean {
    return this.showDeletePrompt;
  }

  @Emit('cancel')
  cancelDelete(): boolean {
    this.showDeletePrompt = false;
    return true;
  }

  @Emit('show-current-preview')
  showCurrentPreview(): NoteHighlight {
    return this.note;
  }

  @Emit('close-preview')
  closePreview(): boolean {
    return true;
  }

  setDisableDeleteNotePromptTime(time: Date): void {
    localConfig.setDisableDeleteNotePromptTime(time);
  }

  initDelete(): void {
    const currentDate = new Date();
    const deleteModeActive =
      this.disableDeleteNotePromptTime &&
      currentDate.getTime() < this.disableDeleteNotePromptTime;

    if (deleteModeActive) {
      this.deleteNote();
    } else {
      this.showDeletePrompt = true;
      this.keepOpenWhenPromptActive();
    }
  }

  handleMouseOver(): void {
    this.showActions = true;
  }

  handleMouseLeave(): void {
    this.showActions = false;
  }

  onScroll({ target: { scrollTop } }) {
    // We determine if content has scrolled, to set some styling
    this.contentIsScrolled = scrollTop > 0;
  }

  mounted() {
    KeyHandleEventBus.$on('escape', this.closePreview);
  }

  destroyed() {
    KeyHandleEventBus.$off('escape', this.closePreview);
  }

  private findPageInTeacherMenu(
    children: TeacherMenuLink[],
    pId: string
  ): TeacherMenuLink | null {
    if (!pId || !children) {
      return;
    }

    let menuChild: TeacherMenuLink | null = null;

    children.forEach((child: TeacherMenuLink) => {
      if (child.uid === Number(pId)) {
        menuChild = child;
      } else {
        this.findPageInTeacherMenu(child.children, pId);
      }
    });

    return menuChild;
  }
}
